import { memo as _$memo } from "@opentui/solid";
import { createTextNode as _$createTextNode } from "@opentui/solid";
import { insertNode as _$insertNode } from "@opentui/solid";
import { effect as _$effect } from "@opentui/solid";
import { insert as _$insert } from "@opentui/solid";
import { setProp as _$setProp } from "@opentui/solid";
import { createElement as _$createElement } from "@opentui/solid";
import { mergeProps as _$mergeProps } from "@opentui/solid";
import { createComponent as _$createComponent } from "@opentui/solid";
import { render, useKeyboard, useRenderer, useTerminalDimensions } from "@opentui/solid";
import { Clipboard } from "@tui/util/clipboard";
import { Selection } from "@tui/util/selection";
import { MouseButton, TextAttributes } from "@opentui/core";
import { RouteProvider, useRoute } from "@tui/context/route";
import { Switch, Match, createEffect, untrack, ErrorBoundary, createSignal, onMount, batch, on } from "solid-js";
import { win32DisableProcessedInput, win32FlushInputBuffer, win32InstallCtrlCGuard } from "./win32";
import { Installation } from "@/installation";
import { Flag } from "@/flag/flag";
import { DialogProvider, useDialog } from "@tui/ui/dialog";
import { DialogProvider as DialogProviderList } from "@tui/component/dialog-provider";
import { SDKProvider, useSDK } from "@tui/context/sdk";
import { SyncProvider, useSync } from "@tui/context/sync";
import { LocalProvider, useLocal } from "@tui/context/local";
import { DialogModel, useConnected } from "@tui/component/dialog-model";
import { DialogMcp } from "@tui/component/dialog-mcp";
import { DialogStatus } from "@tui/component/dialog-status";
import { DialogThemeList } from "@tui/component/dialog-theme-list";
import { DialogHelp } from "./ui/dialog-help";
import { CommandProvider, useCommandDialog } from "@tui/component/dialog-command";
import { DialogAgent } from "@tui/component/dialog-agent";
import { DialogSessionList } from "@tui/component/dialog-session-list";
import { DialogWorkspaceList } from "@tui/component/dialog-workspace-list";
import { KeybindProvider } from "@tui/context/keybind";
import { ThemeProvider, useTheme } from "@tui/context/theme";
import { Home } from "@tui/routes/home";
import { Session } from "@tui/routes/session";
import { PromptHistoryProvider } from "./component/prompt/history";
import { FrecencyProvider } from "./component/prompt/frecency";
import { PromptStashProvider } from "./component/prompt/stash";
import { DialogAlert } from "./ui/dialog-alert";
import { ToastProvider, useToast } from "./ui/toast";
import { ExitProvider, useExit } from "./context/exit";
import { Session as SessionApi } from "@/session";
import { TuiEvent } from "./event";
import { KVProvider, useKV } from "./context/kv";
import { Provider } from "@/provider/provider";
import { ArgsProvider, useArgs } from "./context/args";
import open from "open";
import { writeHeapSnapshot } from "v8";
import { PromptRefProvider, usePromptRef } from "./context/prompt";
import { TuiConfigProvider } from "./context/tui-config";
async function getTerminalBackgroundColor() {
  // can't set raw mode if not a TTY
  if (!process.stdin.isTTY) return "dark";
  return new Promise(resolve => {
    let timeout;
    const cleanup = () => {
      process.stdin.setRawMode(false);
      process.stdin.removeListener("data", handler);
      clearTimeout(timeout);
    };
    const handler = data => {
      const str = data.toString();
      const match = str.match(/\x1b]11;([^\x07\x1b]+)/);
      if (match) {
        cleanup();
        const color = match[1];
        // Parse RGB values from color string
        // Formats: rgb:RR/GG/BB or #RRGGBB or rgb(R,G,B)
        let r = 0,
          g = 0,
          b = 0;
        if (color.startsWith("rgb:")) {
          const parts = color.substring(4).split("/");
          r = parseInt(parts[0], 16) >> 8; // Convert 16-bit to 8-bit
          g = parseInt(parts[1], 16) >> 8; // Convert 16-bit to 8-bit
          b = parseInt(parts[2], 16) >> 8; // Convert 16-bit to 8-bit
        } else if (color.startsWith("#")) {
          r = parseInt(color.substring(1, 3), 16);
          g = parseInt(color.substring(3, 5), 16);
          b = parseInt(color.substring(5, 7), 16);
        } else if (color.startsWith("rgb(")) {
          const parts = color.substring(4, color.length - 1).split(",");
          r = parseInt(parts[0]);
          g = parseInt(parts[1]);
          b = parseInt(parts[2]);
        }

        // Calculate luminance using relative luminance formula
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

        // Determine if dark or light based on luminance threshold
        resolve(luminance > 0.5 ? "light" : "dark");
      }
    };
    process.stdin.setRawMode(true);
    process.stdin.on("data", handler);
    process.stdout.write("\x1b]11;?\x07");
    timeout = setTimeout(() => {
      cleanup();
      resolve("dark");
    }, 1000);
  });
}
export function tui(input) {
  // promise to prevent immediate exit
  return new Promise(async resolve => {
    const unguard = win32InstallCtrlCGuard();
    win32DisableProcessedInput();
    const mode = await getTerminalBackgroundColor();

    // Re-clear after getTerminalBackgroundColor() — setRawMode(false) restores
    // the original console mode which re-enables ENABLE_PROCESSED_INPUT.
    win32DisableProcessedInput();
    const onExit = async () => {
      unguard?.();
      resolve();
    };
    render(() => {
      return _$createComponent(ErrorBoundary, {
        fallback: (error, reset) => _$createComponent(ErrorComponent, {
          error: error,
          reset: reset,
          onExit: onExit,
          mode: mode
        }),
        get children() {
          return _$createComponent(ArgsProvider, _$mergeProps(() => input.args, {
            get children() {
              return _$createComponent(ExitProvider, {
                onExit: onExit,
                get children() {
                  return _$createComponent(KVProvider, {
                    get children() {
                      return _$createComponent(ToastProvider, {
                        get children() {
                          return _$createComponent(RouteProvider, {
                            get children() {
                              return _$createComponent(TuiConfigProvider, {
                                get config() {
                                  return input.config;
                                },
                                get children() {
                                  return _$createComponent(SDKProvider, {
                                    get url() {
                                      return input.url;
                                    },
                                    get directory() {
                                      return input.directory;
                                    },
                                    get fetch() {
                                      return input.fetch;
                                    },
                                    get headers() {
                                      return input.headers;
                                    },
                                    get events() {
                                      return input.events;
                                    },
                                    get children() {
                                      return _$createComponent(SyncProvider, {
                                        get children() {
                                          return _$createComponent(ThemeProvider, {
                                            mode: mode,
                                            get children() {
                                              return _$createComponent(LocalProvider, {
                                                get children() {
                                                  return _$createComponent(KeybindProvider, {
                                                    get children() {
                                                      return _$createComponent(PromptStashProvider, {
                                                        get children() {
                                                          return _$createComponent(DialogProvider, {
                                                            get children() {
                                                              return _$createComponent(CommandProvider, {
                                                                get children() {
                                                                  return _$createComponent(FrecencyProvider, {
                                                                    get children() {
                                                                      return _$createComponent(PromptHistoryProvider, {
                                                                        get children() {
                                                                          return _$createComponent(PromptRefProvider, {
                                                                            get children() {
                                                                              return _$createComponent(App, {});
                                                                            }
                                                                          });
                                                                        }
                                                                      });
                                                                    }
                                                                  });
                                                                }
                                                              });
                                                            }
                                                          });
                                                        }
                                                      });
                                                    }
                                                  });
                                                }
                                              });
                                            }
                                          });
                                        }
                                      });
                                    }
                                  });
                                }
                              });
                            }
                          });
                        }
                      });
                    }
                  });
                }
              });
            }
          }));
        }
      });
    }, {
      targetFps: 60,
      gatherStats: false,
      exitOnCtrlC: false,
      useKittyKeyboard: {},
      autoFocus: false,
      openConsoleOnError: false,
      consoleOptions: {
        keyBindings: [{
          name: "y",
          ctrl: true,
          action: "copy-selection"
        }],
        onCopySelection: text => {
          Clipboard.copy(text).catch(error => {
            console.error(`Failed to copy console selection to clipboard: ${error}`);
          });
        }
      }
    });
  });
}
function App() {
  const route = useRoute();
  const dimensions = useTerminalDimensions();
  const renderer = useRenderer();
  renderer.disableStdoutInterception();
  const dialog = useDialog();
  const local = useLocal();
  const kv = useKV();
  const command = useCommandDialog();
  const sdk = useSDK();
  const toast = useToast();
  const {
    theme,
    mode,
    setMode
  } = useTheme();
  const sync = useSync();
  const exit = useExit();
  const promptRef = usePromptRef();
  useKeyboard(evt => {
    if (!Flag.OPENCODE_EXPERIMENTAL_DISABLE_COPY_ON_SELECT) return;
    if (!renderer.getSelection()) return;

    // Windows Terminal-like behavior:
    // - Ctrl+C copies and dismisses selection
    // - Esc dismisses selection
    // - Most other key input dismisses selection and is passed through
    if (evt.ctrl && evt.name === "c") {
      if (!Selection.copy(renderer, toast)) {
        renderer.clearSelection();
        return;
      }
      evt.preventDefault();
      evt.stopPropagation();
      return;
    }
    if (evt.name === "escape") {
      renderer.clearSelection();
      evt.preventDefault();
      evt.stopPropagation();
      return;
    }
    renderer.clearSelection();
  });

  // Wire up console copy-to-clipboard via opentui's onCopySelection callback
  renderer.console.onCopySelection = async text => {
    if (!text || text.length === 0) return;
    await Clipboard.copy(text).then(() => toast.show({
      message: "Copied to clipboard",
      variant: "info"
    })).catch(toast.error);
    renderer.clearSelection();
  };
  const [terminalTitleEnabled, setTerminalTitleEnabled] = createSignal(kv.get("terminal_title_enabled", true));
  createEffect(() => {
    console.log(JSON.stringify(route.data));
  });

  // Update terminal window title based on current route and session
  createEffect(() => {
    if (!terminalTitleEnabled() || Flag.OPENCODE_DISABLE_TERMINAL_TITLE) return;
    if (route.data.type === "home") {
      renderer.setTerminalTitle("OpenCode");
      return;
    }
    if (route.data.type === "session") {
      const session = sync.session.get(route.data.sessionID);
      if (!session || SessionApi.isDefaultTitle(session.title)) {
        renderer.setTerminalTitle("OpenCode");
        return;
      }

      // Truncate title to 40 chars max
      const title = session.title.length > 40 ? session.title.slice(0, 37) + "..." : session.title;
      renderer.setTerminalTitle(`OC | ${title}`);
    }
  });
  const args = useArgs();
  onMount(() => {
    batch(() => {
      if (args.agent) local.agent.set(args.agent);
      if (args.model) {
        const {
          providerID,
          modelID
        } = Provider.parseModel(args.model);
        if (!providerID || !modelID) return toast.show({
          variant: "warning",
          message: `Invalid model format: ${args.model}`,
          duration: 3000
        });
        local.model.set({
          providerID,
          modelID
        }, {
          recent: true
        });
      }
      // Handle --session without --fork immediately (fork is handled in createEffect below)
      if (args.sessionID && !args.fork) {
        route.navigate({
          type: "session",
          sessionID: args.sessionID
        });
      }
    });
  });
  let continued = false;
  createEffect(() => {
    // When using -c, session list is loaded in blocking phase, so we can navigate at "partial"
    if (continued || sync.status === "loading" || !args.continue) return;
    const match = sync.data.session.toSorted((a, b) => b.time.updated - a.time.updated).find(x => x.parentID === undefined)?.id;
    if (match) {
      continued = true;
      if (args.fork) {
        sdk.client.session.fork({
          sessionID: match
        }).then(result => {
          if (result.data?.id) {
            route.navigate({
              type: "session",
              sessionID: result.data.id
            });
          } else {
            toast.show({
              message: "Failed to fork session",
              variant: "error"
            });
          }
        });
      } else {
        route.navigate({
          type: "session",
          sessionID: match
        });
      }
    }
  });

  // Handle --session with --fork: wait for sync to be fully complete before forking
  // (session list loads in non-blocking phase for --session, so we must wait for "complete"
  // to avoid a race where reconcile overwrites the newly forked session)
  let forked = false;
  createEffect(() => {
    if (forked || sync.status !== "complete" || !args.sessionID || !args.fork) return;
    forked = true;
    sdk.client.session.fork({
      sessionID: args.sessionID
    }).then(result => {
      if (result.data?.id) {
        route.navigate({
          type: "session",
          sessionID: result.data.id
        });
      } else {
        toast.show({
          message: "Failed to fork session",
          variant: "error"
        });
      }
    });
  });
  createEffect(on(() => sync.status === "complete" && sync.data.provider.length === 0, (isEmpty, wasEmpty) => {
    // only trigger when we transition into an empty-provider state
    if (!isEmpty || wasEmpty) return;
    dialog.replace(() => _$createComponent(DialogProviderList, {}));
  }));
  const connected = useConnected();
  command.register(() => [{
    title: "Switch session",
    value: "session.list",
    keybind: "session_list",
    category: "Session",
    suggested: sync.data.session.length > 0,
    slash: {
      name: "sessions",
      aliases: ["resume", "continue"]
    },
    onSelect: () => {
      dialog.replace(() => _$createComponent(DialogSessionList, {}));
    }
  }, ...(Flag.OPENCODE_EXPERIMENTAL_WORKSPACES_TUI ? [{
    title: "Manage workspaces",
    value: "workspace.list",
    category: "Workspace",
    suggested: true,
    slash: {
      name: "workspaces"
    },
    onSelect: () => {
      dialog.replace(() => _$createComponent(DialogWorkspaceList, {}));
    }
  }] : []), {
    title: "New session",
    suggested: route.data.type === "session",
    value: "session.new",
    keybind: "session_new",
    category: "Session",
    slash: {
      name: "new",
      aliases: ["clear"]
    },
    onSelect: () => {
      const current = promptRef.current;
      // Don't require focus - if there's any text, preserve it
      const currentPrompt = current?.current?.input ? current.current : undefined;
      route.navigate({
        type: "home",
        initialPrompt: currentPrompt
      });
      dialog.clear();
    }
  }, {
    title: "Switch model",
    value: "model.list",
    keybind: "model_list",
    suggested: true,
    category: "Agent",
    slash: {
      name: "models"
    },
    onSelect: () => {
      dialog.replace(() => _$createComponent(DialogModel, {}));
    }
  }, {
    title: "Model cycle",
    value: "model.cycle_recent",
    keybind: "model_cycle_recent",
    category: "Agent",
    hidden: true,
    onSelect: () => {
      local.model.cycle(1);
    }
  }, {
    title: "Model cycle reverse",
    value: "model.cycle_recent_reverse",
    keybind: "model_cycle_recent_reverse",
    category: "Agent",
    hidden: true,
    onSelect: () => {
      local.model.cycle(-1);
    }
  }, {
    title: "Favorite cycle",
    value: "model.cycle_favorite",
    keybind: "model_cycle_favorite",
    category: "Agent",
    hidden: true,
    onSelect: () => {
      local.model.cycleFavorite(1);
    }
  }, {
    title: "Favorite cycle reverse",
    value: "model.cycle_favorite_reverse",
    keybind: "model_cycle_favorite_reverse",
    category: "Agent",
    hidden: true,
    onSelect: () => {
      local.model.cycleFavorite(-1);
    }
  }, {
    title: "Switch agent",
    value: "agent.list",
    keybind: "agent_list",
    category: "Agent",
    slash: {
      name: "agents"
    },
    onSelect: () => {
      dialog.replace(() => _$createComponent(DialogAgent, {}));
    }
  }, {
    title: "Toggle MCPs",
    value: "mcp.list",
    category: "Agent",
    slash: {
      name: "mcps"
    },
    onSelect: () => {
      dialog.replace(() => _$createComponent(DialogMcp, {}));
    }
  }, {
    title: "Agent cycle",
    value: "agent.cycle",
    keybind: "agent_cycle",
    category: "Agent",
    hidden: true,
    onSelect: () => {
      local.agent.move(1);
    }
  }, {
    title: "Variant cycle",
    value: "variant.cycle",
    keybind: "variant_cycle",
    category: "Agent",
    hidden: true,
    onSelect: () => {
      local.model.variant.cycle();
    }
  }, {
    title: "Agent cycle reverse",
    value: "agent.cycle.reverse",
    keybind: "agent_cycle_reverse",
    category: "Agent",
    hidden: true,
    onSelect: () => {
      local.agent.move(-1);
    }
  }, {
    title: "Connect provider",
    value: "provider.connect",
    suggested: !connected(),
    slash: {
      name: "connect"
    },
    onSelect: () => {
      dialog.replace(() => _$createComponent(DialogProviderList, {}));
    },
    category: "Provider"
  }, {
    title: "View status",
    keybind: "status_view",
    value: "opencode.status",
    slash: {
      name: "status"
    },
    onSelect: () => {
      dialog.replace(() => _$createComponent(DialogStatus, {}));
    },
    category: "System"
  }, {
    title: "Switch theme",
    value: "theme.switch",
    keybind: "theme_list",
    slash: {
      name: "themes"
    },
    onSelect: () => {
      dialog.replace(() => _$createComponent(DialogThemeList, {}));
    },
    category: "System"
  }, {
    title: "Toggle appearance",
    value: "theme.switch_mode",
    onSelect: dialog => {
      setMode(mode() === "dark" ? "light" : "dark");
      dialog.clear();
    },
    category: "System"
  }, {
    title: "Help",
    value: "help.show",
    slash: {
      name: "help"
    },
    onSelect: () => {
      dialog.replace(() => _$createComponent(DialogHelp, {}));
    },
    category: "System"
  }, {
    title: "Open docs",
    value: "docs.open",
    onSelect: () => {
      open("https://opencode.ai/docs").catch(() => {});
      dialog.clear();
    },
    category: "System"
  }, {
    title: "Exit the app",
    value: "app.exit",
    slash: {
      name: "exit",
      aliases: ["quit", "q"]
    },
    onSelect: () => exit(),
    category: "System"
  }, {
    title: "Toggle debug panel",
    category: "System",
    value: "app.debug",
    onSelect: dialog => {
      renderer.toggleDebugOverlay();
      dialog.clear();
    }
  }, {
    title: "Toggle console",
    category: "System",
    value: "app.console",
    onSelect: dialog => {
      renderer.console.toggle();
      dialog.clear();
    }
  }, {
    title: "Write heap snapshot",
    category: "System",
    value: "app.heap_snapshot",
    onSelect: dialog => {
      const path = writeHeapSnapshot();
      toast.show({
        variant: "info",
        message: `Heap snapshot written to ${path}`,
        duration: 5000
      });
      dialog.clear();
    }
  }, {
    title: "Suspend terminal",
    value: "terminal.suspend",
    keybind: "terminal_suspend",
    category: "System",
    hidden: true,
    onSelect: () => {
      process.once("SIGCONT", () => {
        renderer.resume();
      });
      renderer.suspend();
      // pid=0 means send the signal to all processes in the process group
      process.kill(0, "SIGTSTP");
    }
  }, {
    title: terminalTitleEnabled() ? "Disable terminal title" : "Enable terminal title",
    value: "terminal.title.toggle",
    keybind: "terminal_title_toggle",
    category: "System",
    onSelect: dialog => {
      setTerminalTitleEnabled(prev => {
        const next = !prev;
        kv.set("terminal_title_enabled", next);
        if (!next) renderer.setTerminalTitle("");
        return next;
      });
      dialog.clear();
    }
  }, {
    title: kv.get("animations_enabled", true) ? "Disable animations" : "Enable animations",
    value: "app.toggle.animations",
    category: "System",
    onSelect: dialog => {
      kv.set("animations_enabled", !kv.get("animations_enabled", true));
      dialog.clear();
    }
  }, {
    title: kv.get("diff_wrap_mode", "word") === "word" ? "Disable diff wrapping" : "Enable diff wrapping",
    value: "app.toggle.diffwrap",
    category: "System",
    onSelect: dialog => {
      const current = kv.get("diff_wrap_mode", "word");
      kv.set("diff_wrap_mode", current === "word" ? "none" : "word");
      dialog.clear();
    }
  }]);
  createEffect(() => {
    const currentModel = local.model.current();
    if (!currentModel) return;
    if (currentModel.providerID === "openrouter" && !kv.get("openrouter_warning", false)) {
      untrack(() => {
        DialogAlert.show(dialog, "Warning", "While openrouter is a convenient way to access LLMs your request will often be routed to subpar providers that do not work well in our testing.\n\nFor reliable access to models check out OpenCode Zen\nhttps://opencode.ai/zen").then(() => kv.set("openrouter_warning", true));
      });
    }
  });
  sdk.event.on(TuiEvent.CommandExecute.type, evt => {
    command.trigger(evt.properties.command);
  });
  sdk.event.on(TuiEvent.ToastShow.type, evt => {
    toast.show({
      title: evt.properties.title,
      message: evt.properties.message,
      variant: evt.properties.variant,
      duration: evt.properties.duration
    });
  });
  sdk.event.on(TuiEvent.SessionSelect.type, evt => {
    route.navigate({
      type: "session",
      sessionID: evt.properties.sessionID
    });
  });
  sdk.event.on(SessionApi.Event.Deleted.type, evt => {
    if (route.data.type === "session" && route.data.sessionID === evt.properties.info.id) {
      route.navigate({
        type: "home"
      });
      toast.show({
        variant: "info",
        message: "The current session was deleted"
      });
    }
  });
  sdk.event.on(SessionApi.Event.Error.type, evt => {
    const error = evt.properties.error;
    if (error && typeof error === "object" && error.name === "MessageAbortedError") return;
    const message = (() => {
      if (!error) return "An error occurred";
      if (typeof error === "object") {
        const data = error.data;
        if ("message" in data && typeof data.message === "string") {
          return data.message;
        }
      }
      return String(error);
    })();
    toast.show({
      variant: "error",
      message,
      duration: 5000
    });
  });
  sdk.event.on(Installation.Event.UpdateAvailable.type, evt => {
    toast.show({
      variant: "info",
      title: "Update Available",
      message: `OpenCode v${evt.properties.version} is available. Run 'opencode upgrade' to update manually.`,
      duration: 10000
    });
  });
  return (() => {
    var _el$ = _$createElement("box");
    _$setProp(_el$, "onMouseDown", evt => {
      if (!Flag.OPENCODE_EXPERIMENTAL_DISABLE_COPY_ON_SELECT) return;
      if (evt.button !== MouseButton.RIGHT) return;
      if (!Selection.copy(renderer, toast)) return;
      evt.preventDefault();
      evt.stopPropagation();
    });
    _$insert(_el$, _$createComponent(Switch, {
      get children() {
        return [_$createComponent(Match, {
          get when() {
            return route.data.type === "home";
          },
          get children() {
            return _$createComponent(Home, {});
          }
        }), _$createComponent(Match, {
          get when() {
            return route.data.type === "session";
          },
          get children() {
            return _$createComponent(Session, {});
          }
        })];
      }
    }));
    _$effect(_p$ => {
      var _v$ = dimensions().width,
        _v$2 = dimensions().height,
        _v$3 = theme.background,
        _v$4 = Flag.OPENCODE_EXPERIMENTAL_DISABLE_COPY_ON_SELECT ? undefined : () => Selection.copy(renderer, toast);
      _v$ !== _p$.e && (_p$.e = _$setProp(_el$, "width", _v$, _p$.e));
      _v$2 !== _p$.t && (_p$.t = _$setProp(_el$, "height", _v$2, _p$.t));
      _v$3 !== _p$.a && (_p$.a = _$setProp(_el$, "backgroundColor", _v$3, _p$.a));
      _v$4 !== _p$.o && (_p$.o = _$setProp(_el$, "onMouseUp", _v$4, _p$.o));
      return _p$;
    }, {
      e: undefined,
      t: undefined,
      a: undefined,
      o: undefined
    });
    return _el$;
  })();
}
function ErrorComponent(props) {
  const term = useTerminalDimensions();
  const renderer = useRenderer();
  const handleExit = async () => {
    renderer.setTerminalTitle("");
    renderer.destroy();
    win32FlushInputBuffer();
    await props.onExit();
  };
  useKeyboard(evt => {
    if (evt.ctrl && evt.name === "c") {
      handleExit();
    }
  });
  const [copied, setCopied] = createSignal(false);
  const issueURL = new URL("https://github.com/anomalyco/opencode/issues/new?template=bug-report.yml");

  // Choose safe fallback colors per mode since theme context may not be available
  const isLight = props.mode === "light";
  const colors = {
    bg: isLight ? "#ffffff" : "#0a0a0a",
    text: isLight ? "#1a1a1a" : "#eeeeee",
    muted: isLight ? "#8a8a8a" : "#808080",
    primary: isLight ? "#3b7dd8" : "#fab283"
  };
  if (props.error.message) {
    issueURL.searchParams.set("title", `opentui: fatal: ${props.error.message}`);
  }
  if (props.error.stack) {
    issueURL.searchParams.set("description", "```\n" + props.error.stack.substring(0, 6000 - issueURL.toString().length) + "...\n```");
  }
  issueURL.searchParams.set("opencode-version", Installation.VERSION);
  const copyIssueURL = () => {
    Clipboard.copy(issueURL.toString()).then(() => {
      setCopied(true);
    });
  };
  return (() => {
    var _el$2 = _$createElement("box"),
      _el$3 = _$createElement("box"),
      _el$4 = _$createElement("text"),
      _el$6 = _$createElement("box"),
      _el$7 = _$createElement("text"),
      _el$9 = _$createElement("box"),
      _el$0 = _$createElement("text"),
      _el$10 = _$createElement("box"),
      _el$11 = _$createElement("text"),
      _el$13 = _$createElement("box"),
      _el$14 = _$createElement("text"),
      _el$16 = _$createElement("scrollbox"),
      _el$17 = _$createElement("text"),
      _el$18 = _$createElement("text");
    _$insertNode(_el$2, _el$3);
    _$insertNode(_el$2, _el$9);
    _$insertNode(_el$2, _el$16);
    _$insertNode(_el$2, _el$18);
    _$setProp(_el$2, "flexDirection", "column");
    _$setProp(_el$2, "gap", 1);
    _$insertNode(_el$3, _el$4);
    _$insertNode(_el$3, _el$6);
    _$setProp(_el$3, "flexDirection", "row");
    _$setProp(_el$3, "gap", 1);
    _$setProp(_el$3, "alignItems", "center");
    _$insertNode(_el$4, _$createTextNode(`Please report an issue.`));
    _$insertNode(_el$6, _el$7);
    _$setProp(_el$6, "onMouseUp", copyIssueURL);
    _$setProp(_el$6, "padding", 1);
    _$insertNode(_el$7, _$createTextNode(`Copy issue URL (exception info pre-filled)`));
    _$insert(_el$3, (() => {
      var _c$ = _$memo(() => !!copied());
      return () => _c$() && (() => {
        var _el$19 = _$createElement("text");
        _$insertNode(_el$19, _$createTextNode(`Successfully copied`));
        _$effect(_$p => _$setProp(_el$19, "fg", colors.muted, _$p));
        return _el$19;
      })();
    })(), null);
    _$insertNode(_el$9, _el$0);
    _$insertNode(_el$9, _el$10);
    _$insertNode(_el$9, _el$13);
    _$setProp(_el$9, "flexDirection", "row");
    _$setProp(_el$9, "gap", 2);
    _$setProp(_el$9, "alignItems", "center");
    _$insertNode(_el$0, _$createTextNode(`A fatal error occurred!`));
    _$insertNode(_el$10, _el$11);
    _$setProp(_el$10, "padding", 1);
    _$insertNode(_el$11, _$createTextNode(`Reset TUI`));
    _$insertNode(_el$13, _el$14);
    _$setProp(_el$13, "onMouseUp", handleExit);
    _$setProp(_el$13, "padding", 1);
    _$insertNode(_el$14, _$createTextNode(`Exit`));
    _$insertNode(_el$16, _el$17);
    _$insert(_el$17, () => props.error.stack);
    _$insert(_el$18, () => props.error.message);
    _$effect(_p$ => {
      var _v$5 = colors.bg,
        _v$6 = TextAttributes.BOLD,
        _v$7 = colors.text,
        _v$8 = colors.primary,
        _v$9 = TextAttributes.BOLD,
        _v$0 = colors.bg,
        _v$1 = colors.text,
        _v$10 = props.reset,
        _v$11 = colors.primary,
        _v$12 = colors.bg,
        _v$13 = colors.primary,
        _v$14 = colors.bg,
        _v$15 = Math.floor(term().height * 0.7),
        _v$16 = colors.muted,
        _v$17 = colors.text;
      _v$5 !== _p$.e && (_p$.e = _$setProp(_el$2, "backgroundColor", _v$5, _p$.e));
      _v$6 !== _p$.t && (_p$.t = _$setProp(_el$4, "attributes", _v$6, _p$.t));
      _v$7 !== _p$.a && (_p$.a = _$setProp(_el$4, "fg", _v$7, _p$.a));
      _v$8 !== _p$.o && (_p$.o = _$setProp(_el$6, "backgroundColor", _v$8, _p$.o));
      _v$9 !== _p$.i && (_p$.i = _$setProp(_el$7, "attributes", _v$9, _p$.i));
      _v$0 !== _p$.n && (_p$.n = _$setProp(_el$7, "fg", _v$0, _p$.n));
      _v$1 !== _p$.s && (_p$.s = _$setProp(_el$0, "fg", _v$1, _p$.s));
      _v$10 !== _p$.h && (_p$.h = _$setProp(_el$10, "onMouseUp", _v$10, _p$.h));
      _v$11 !== _p$.r && (_p$.r = _$setProp(_el$10, "backgroundColor", _v$11, _p$.r));
      _v$12 !== _p$.d && (_p$.d = _$setProp(_el$11, "fg", _v$12, _p$.d));
      _v$13 !== _p$.l && (_p$.l = _$setProp(_el$13, "backgroundColor", _v$13, _p$.l));
      _v$14 !== _p$.u && (_p$.u = _$setProp(_el$14, "fg", _v$14, _p$.u));
      _v$15 !== _p$.c && (_p$.c = _$setProp(_el$16, "height", _v$15, _p$.c));
      _v$16 !== _p$.w && (_p$.w = _$setProp(_el$17, "fg", _v$16, _p$.w));
      _v$17 !== _p$.m && (_p$.m = _$setProp(_el$18, "fg", _v$17, _p$.m));
      return _p$;
    }, {
      e: undefined,
      t: undefined,
      a: undefined,
      o: undefined,
      i: undefined,
      n: undefined,
      s: undefined,
      h: undefined,
      r: undefined,
      d: undefined,
      l: undefined,
      u: undefined,
      c: undefined,
      w: undefined,
      m: undefined
    });
    return _el$2;
  })();
}